import { FormHelperText, Stack, Typography, Box, Paper, Button } from '@mui/material';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';

import LoadingButton from '@/@mantis/components/@extended/LoadingButton';
import {
  ApiRequest_Create_PropertyLocation,
  useCreatePropertyLocation,
} from '@/api/endpoints/propertyLocation/useCreatePropertyLocation';
import { useGetUserReferral } from '@/api/endpoints/user/useUserReferral';
import { PropertyLocationType } from '@/api/enums';
import { LogOut01Icon } from '@/assets/icons/LogOut01Icon';
import { PublicRoutes, OnboardingRoutes } from '@/config/routes';
import { useAuth } from '@/context/AuthProvider';
import { useOnboardingProgress } from '@/context/OnboardingProgressProvider';
import { usePropertyManager } from '@/context/PropertyManagerProvider';
import { getNextOnboardingStep } from '@/utils/onboarding';

import FormAddressAutocomplete from '@/components/form/FormAddressAutocomplete';
import FormSelect from '@/components/form/FormSelect';
import FormSwitch from '@/components/form/FormSwitch';
import FormTextField from '@/components/form/FormTextField';
import MapboxAddressMap from '@/components/mapbox/MapboxAddressMap';
import {
  propertyLocationDeclaredUnitsCountOptions,
  propertyLocationTypeOptions,
} from '@/components/propertyLocation/options';

import SplitMuiPage from '../common/SplitMuiPage';

const OnboardingLocationPage: React.FC = () => {
  const { refetch, isFetching } = usePropertyManager();
  const { onboardingProgress } = useOnboardingProgress();
  const { signOut, user } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const { data: userReferral } = useGetUserReferral();

  // Extract campaign and returnUrl from query parameters
  const urlParams = new URLSearchParams(location.search);
  const campaign = urlParams.get('campaign');
  const returnUrl = urlParams.get('returnUrl');

  // Check if user came from a share link or has a pending referral
  const [hasSkipOption, setHasSkipOption] = useState(false);

  const handleLogout = useCallback(() => {
    signOut().then(() => {
      history.push(PublicRoutes.login);
    });
  }, [signOut, history]);

  useEffect(() => {
    refetch();
    // Check if user came from a share link or has a pending referral
    const shareRedirectUrl = localStorage.getItem('shareRedirectUrl');
    const postOnboardingRedirect = localStorage.getItem('postOnboardingRedirect');
    const hasPendingReferral = userReferral && !userReferral.is_redirected;

    if (
      (shareRedirectUrl && shareRedirectUrl.includes('/share/')) ||
      hasPendingReferral ||
      postOnboardingRedirect
    ) {
      setHasSkipOption(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userReferral]);

  const isPropertyOwner =
    campaign === 'property_owner' || user?.user_metadata.campaign === 'property_owner';

  const defaultValues = useMemo(
    () => ({
      name: '',
      address_text: '',
      type: isPropertyOwner ? PropertyLocationType.OTHER : undefined,
      declared_units_count: undefined,
      metadata: isPropertyOwner ? { usage: '' } : undefined,
      is_rental_property: false,
    }),
    [isPropertyOwner]
  );

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ApiRequest_Create_PropertyLocation>({
    defaultValues,
    mode: 'onBlur',
  });

  const [formError, setFormError] = useState<Error | null>(null);

  const addressText = watch('address_text');
  const propertyLocationType = watch('type');

  const createPropertyLocationMutation = useCreatePropertyLocation({
    onSuccess: async data => {
      // Refetch property locations to update the context
      await refetch();
      
      // If there's a returnUrl, navigate back to it
      if (returnUrl) {
        history.push(decodeURIComponent(returnUrl));
      } else {
        // Otherwise, continue with the normal onboarding flow
        history.replace(
          ...getNextOnboardingStep(
            OnboardingRoutes.propertyLocationCreate,
            {
              ...onboardingProgress,
              firstPropertyLocationId: data.id,
              hasPropertyLocation: true,
            },
            campaign
          )
        );
      }
    },
    onError: error => {
      setFormError((error?.response?.data ?? error) as Error);
    },
  });

  const onSubmit = useCallback(
    (data: ApiRequest_Create_PropertyLocation) => createPropertyLocationMutation.mutateAsync(data),
    [createPropertyLocationMutation]
  );

  const emailPreview = useMemo(() => {
    if (!addressText) return null;

    // Extract state from address (this is a simplified version)
    const stateMatch = addressText.match(/,\s*([A-Z]{2})/);
    const state = stateMatch ? stateMatch[1] : '';

    // Extract city from address
    const cityMatch = addressText.match(/,\s*([^,]+),\s*[A-Z]{2}/);
    const city = cityMatch ? cityMatch[1].trim() : '';

    // Generate sample name (remove spaces and special characters)
    // Just use the first part of the address (street number and name)
    const streetPart = addressText.split(',')[0]?.trim();
    const formattedName = streetPart?.replace(/[^a-zA-Z0-9]/g, '') || 'property';

    // Always provide at least one fallback option
    const options = [
      `${formattedName}@mail.terr.ai`,
      state ? `${formattedName}.${state}@mail.terr.ai` : null,
      city ? `${formattedName}.${city}@mail.terr.ai` : null,
    ].filter(Boolean);

    return options[0]?.toLowerCase() || null; // Always return at least the first option
  }, [addressText]);

  return (
    <SplitMuiPage
      sideContent={<MapboxAddressMap addressText={addressText} />}
      isLoading={isFetching}
      contentWidth="35rem"
    >
      <Box display="flex" justifyContent="flex-end" width="100%" mb={2}>
        <Button color="primary" size="small" onClick={handleLogout} startIcon={<LogOut01Icon />}>
          Logout
        </Button>
      </Box>
      <Stack component="form" onSubmit={handleSubmit(onSubmit)} spacing={3} width="100%">
        <Stack spacing={3.5} mb={1}>
          <Typography variant="h3">
            {isPropertyOwner ? 'Add your real estate' : 'Add your rental property'}
          </Typography>

          <Typography variant="body2">
            {(() => {
              if (hasSkipOption) {
                return 'You can add your property now to unlock property management features, or skip this step and join the group directly.';
              }
              if (isPropertyOwner) {
                return "Whether it's your personal home, a rental unit, or a commercial space — adding your property helps us organize everything in one place. From smart document storage to automated reminders, Terr.ai works behind the scenes to keep you in control.";
              }
              return 'This is the location that you are renting. A unique email address for this location will be generated from the rental property address you enter. Your tenants will use this email to contact you.';
            })()}
          </Typography>
        </Stack>

        <Stack spacing={2.5}>
          <FormTextField
            label="Enter a name to help you identify this property (e.g Florida Beach House)"
            placeholder="Enter property name"
            error={!!errors.name}
            helperText={errors.name?.message?.toString()}
            required
            {...register('name', {
              required: 'Property Name is required',
            })}
            control={control}
          />

          <FormAddressAutocomplete
            label={
              isPropertyOwner
                ? 'Address of the property (e.g. 123 Main St, Miami, FL 33130)'
                : 'Address for the property you are renting (e.g. 123 Main St, Miami, FL 33130)'
            }
            placeholder="Enter property address"
            error={!!errors.address_text}
            helperText={errors.address_text?.message?.toString()}
            required
            {...register('address_text', {
              required: 'Property Address is required',
            })}
            control={control}
          />

          {emailPreview && (
            <Box sx={{ mt: 1, mb: 1 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Email Preview:
                </Typography>
                <Typography variant="body2" fontWeight="medium" sx={{ mt: 0.5 }}>
                  {emailPreview}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  This is an example of the email address that will be generated for this property
                  which you will use to communicate with your tenants as well as providers.
                </Typography>
              </Paper>
            </Box>
          )}

          <FormSwitch
            label="Are you renting this property?"
            controlLabel="Yes, this is a rental property"
            {...register('is_rental_property')}
            control={control}
          />

          {isPropertyOwner && (
            <FormTextField
              label="How do you use this property?"
              placeholder="e.g., I live here or its my primary residence, This is my dorm, I am a paying guest, I rent this space, etc."
              tooltipText="We need this information to offer personalized responses based on how you use the property."
              error={!!errors.metadata?.usage}
              helperText={errors.metadata?.usage?.message?.toString()}
              {...register('metadata.usage')}
              control={control}
              multiline
              rows={2}
            />
          )}

          {!isPropertyOwner && (
            <FormSelect
              label="Property Type"
              placeholder="Select property type"
              error={!!errors.type}
              helperText={errors.type?.message?.toString()}
              required={!isPropertyOwner}
              {...register('type', {
                required: !isPropertyOwner && 'Property Type is required',
              })}
              control={control}
              options={propertyLocationTypeOptions}
            />
          )}

          {propertyLocationType === PropertyLocationType.MULTI_UNIT && (
            <FormSelect
              label="Units Count"
              placeholder="Select units count"
              error={!!errors.declared_units_count}
              helperText={errors.declared_units_count?.message?.toString()}
              required
              {...register('declared_units_count', {
                required: 'Units Count is required',
              })}
              control={control}
              options={propertyLocationDeclaredUnitsCountOptions}
            />
          )}

          {!!formError && <FormHelperText error>{formError.message}</FormHelperText>}
        </Stack>

        <Stack spacing={1} mb={1}>
          <LoadingButton loading={isSubmitting} fullWidth variant="contained" type="submit">
            Next
          </LoadingButton>

          {hasSkipOption && (
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                // If there's a returnUrl from query params, use it first
                if (returnUrl) {
                  history.push(decodeURIComponent(returnUrl));
                } else if (userReferral && userReferral.referral_url) {
                  // Then check for user referral
                  history.push(userReferral.referral_url);
                } else {
                  // Check for post onboarding redirect
                  const postOnboardingRedirect = localStorage.getItem('postOnboardingRedirect');
                  if (postOnboardingRedirect) {
                    localStorage.removeItem('postOnboardingRedirect');
                    history.push(postOnboardingRedirect);
                  } else {
                    // Fallback to share redirect URL
                    const shareRedirectUrl = localStorage.getItem('shareRedirectUrl');
                    if (shareRedirectUrl) {
                      localStorage.removeItem('shareRedirectUrl');
                      history.push(shareRedirectUrl);
                    } else {
                      // Fallback to dashboard if no URL found
                      history.push('/property-manager');
                    }
                  }
                }
              }}
            >
              Skip for now
            </Button>
          )}
        </Stack>
      </Stack>
    </SplitMuiPage>
  );
};

export default OnboardingLocationPage;
